/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { AppBar, Box, Container, Fab, List, Stack, Typography } from "@mui/material";
import { StyleguideSectionProps } from "@/src/styleguide/components/StyleguideSection/StyleguideSection.tsx";
import { useRef, useState } from "react";
import { ArrowUpward } from "@mui/icons-material";
import { motion, useMotionValueEvent, useScroll } from "motion/react";
import { styleguideStyle } from "@/src/styleguide/components/Styleguide/styleguideStyle.ts";
import StyleguideSectionRenderer from "@/src/styleguide/components/Styleguide/StyleguideSectionRenderer.tsx";
import StyleguideLinkRenderer from "@/src/styleguide/components/Styleguide/StyleguideLinkRenderer.tsx";
import StyleguideStageBackgroundLogo from "@/src/styleguide/components/Styleguide/StyleguideStageBackgroundLogo.tsx";
import StyleguideProvider, {
  useStyleguideContext
} from "@/src/styleguide/components/Styleguide/StyleguideProvider.tsx";

const MotionFab = motion.create(Fab);

export type StyleguideInnerProps = {
  sections: Array<Omit<StyleguideSectionProps, "level"> & { subSections?: Omit<StyleguideInnerProps["sections"], "subSections"> }>;
};

function StyleguideInner({ sections }: StyleguideInnerProps) {
  const { activeSection } = useStyleguideContext();
  const styleguideRef = useRef<HTMLDivElement>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { scrollY } = useScroll({
    target: styleguideRef,
  });

  useMotionValueEvent(scrollY, "change", (latestValue) => {
    if (latestValue >= 100) {
      setShowBackToTop(true);
    } else {
      setShowBackToTop(false);
    }
  });

  const handleBackToTop = () => {
    window.scrollTo({ top: 0 });
    styleguideRef.current?.focus();
  };

  return (
    <Stack spacing={"1rem"} sx={{ alignItems: "center" }} ref={styleguideRef} tabIndex={-1}>
      {!!activeSection?.title && (
        <AppBar sx={styleguideStyle.appBar}>
          <Container>
            <Typography variant={"h3"}>{activeSection.title}</Typography>
          </Container>
        </AppBar>
      )}
      <Box sx={styleguideStyle.stage}>
        <Container>
          <Typography variant={"h4"} component={"h1"}>
            <strong>Legalbird Client Styleguide</strong>
          </Typography>
          <List>
            {sections.map((section) => (
              <StyleguideLinkRenderer section={section} key={section.id} />
            ))}
          </List>
        </Container>
        <StyleguideStageBackgroundLogo sx={styleguideStyle.backgroundLogo} />
      </Box>
      {sections.map((section) => (
        <StyleguideSectionRenderer section={section} key={section.id} />
      ))}
      <MotionFab
        color="primary"
        onClick={handleBackToTop}
        sx={styleguideStyle.fab}
        transition={{
          bounce: 0,
        }}
        animate={{
          opacity: showBackToTop ? 1 : 0,
          translateX: "50%",
          translateY: showBackToTop ? "0" : "100%",
        }}
      >
        <ArrowUpward />
      </MotionFab>
    </Stack>
  );
}

export default function Styleguide(props: StyleguideInnerProps) {
  return (
    <StyleguideProvider>
      <StyleguideInner {...props} />
    </StyleguideProvider>
  );
}
